import { Dispatch, SetStateAction } from "react";
import {
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import currency from "currency.js";
import {
  GetMarkets200Response,
  GetOrders200Response,
} from "../../codegen-api";
import { FormValidatorKeysEnum } from "../../enums/form";
import { AssetResponse } from "../../utils/asset";
import {
  alignPriceToStep,
  floorToStepString,
  roundToStepSize,
} from "../../utils/format";
import { IContractPriceStep } from "../../utils/instruments";
import { knownPrice } from "../../utils/unknownNumbers";
import {
  PriceInput as OptionsPriceInput,
  SizeInput as OptionsSizeInput,
} from "../TradeForm/OptionsTradeForm/form";
import {
  PerpFormFieldKeyEnum,
  PriceInput as PerpsPriceInput,
  OrderSizeInput as PerpsSizeInput,
  TriggerPriceInput,
} from "../TradeForm/PerpsTradeForm/form";
import { editPriceDirection, floorUsdFieldValue } from "./EditOrderModal.helpers";

/** A size field floored to `amount_step`; an empty field stays empty. */
const floorSizeField = (value: string, step: number, precision: number) =>
  String(value ?? "").trim() === ""
    ? ""
    : floorToStepString(value, step, precision);

interface IRenderPriceSizeInputsParams {
  isOption: boolean;
  contractPriceStep: IContractPriceStep;
  register: UseFormRegister<FieldValues>;
  loading: boolean;
  setValue: UseFormSetValue<FieldValues>;
  errors: FieldErrors<FieldValues>;
  verifyNotEnoughBalance: () => boolean;
  verifyOrderValueTooSmall: (size: string) => boolean;
  order?: GetOrders200Response;
  instrument?: GetMarkets200Response;
  triggerPriceValidateFn: { [key: string]: (value: any) => boolean };
  perpAsset?: AssetResponse;
  setPerpAsset: Dispatch<SetStateAction<AssetResponse | undefined>>;
  price: any;
  unfilledAmount: string;
  showAmountValidation: boolean;
  /** USD mode: the contract size a typed USD figure is sent as. */
  usdContractSizeFor?: (usd: string) => string;
}

export function renderPriceSizeInputs({
  isOption,
  contractPriceStep,
  register,
  loading,
  setValue,
  errors,
  verifyNotEnoughBalance,
  verifyOrderValueTooSmall,
  order,
  instrument,
  triggerPriceValidateFn,
  perpAsset,
  setPerpAsset,
  price,
  unfilledAmount,
  showAmountValidation,
  usdContractSizeFor,
}: IRenderPriceSizeInputsParams) {
  // Snap a typed price onto `price_step` on the side that is safe for the order.
  const alignPriceField = (value: string) =>
    alignPriceToStep(
      value,
      contractPriceStep.price_step,
      contractPriceStep.price_precision,
      editPriceDirection(order?.side)
    );

  if (isOption) {
    return (
      <>
        <OptionsPriceInput
          minPrice={contractPriceStep.price_step}
          register={register("price", {
            disabled: loading,
            required: true,
            validate: {
              [FormValidatorKeysEnum.moreThanZero]: (v) => parseFloat(v) > 0,
              [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
                roundToStepSize(
                  v,
                  contractPriceStep.price_step,
                  contractPriceStep.price_precision
                ) >= contractPriceStep.price_step,
            },
            onBlur(event) {
              setValue("price", alignPriceField(event.target.value));
            },
          })}
          errors={errors}
        />
        <OptionsSizeInput
          minAmountSize={contractPriceStep.amount_step || 0}
          register={register("amount", {
            disabled: loading,
            required: true,
            validate: {
              [FormValidatorKeysEnum.moreThanZero]: (v) => parseFloat(v) > 0,
              [FormValidatorKeysEnum.notEnoughBalance]:
                verifyNotEnoughBalance,
              [FormValidatorKeysEnum.orderValueTooSmall]:
                verifyOrderValueTooSmall,
              [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
                roundToStepSize(
                  v,
                  contractPriceStep.amount_step,
                  contractPriceStep.amount_precision
                ) >= contractPriceStep.amount_step,
            },
            onBlur(event) {
              setValue(
                "amount",
                floorSizeField(
                  event.target.value,
                  contractPriceStep.amount_step,
                  contractPriceStep.amount_precision
                )
              );
            },
          })}
          style={{ paddingBottom: 0 }}
          errors={errors}
        />
      </>
    );
  }
  return (
    <>
      {/* Only show trigger price if is stop order */}
      {order?.trigger && (
        <TriggerPriceInput
          placeholder={currency(order.trigger).format()}
          markPrice={
            knownPrice(instrument?.mark_price) !== undefined
              ? Number(instrument?.mark_price).toFixed(
                  contractPriceStep.price_precision
                )
              : undefined
          }
          minPrice={contractPriceStep.price_step}
          register={register(PerpFormFieldKeyEnum.TRIGGER_PRICE, {
            disabled: loading,
            required: true,
            validate: {
              ...triggerPriceValidateFn,
              [FormValidatorKeysEnum.moreThanZero]: (v) => parseFloat(v) > 0,
              [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
                roundToStepSize(
                  Number(v),
                  contractPriceStep.price_step,
                  contractPriceStep.price_precision
                ) >= contractPriceStep.price_step,
            },
            onBlur(event) {
              // Floored onto `price_step` as before (plain decimals, empty stays
              // empty); not moved by side like a limit price.
              setValue(
                PerpFormFieldKeyEnum.TRIGGER_PRICE,
                alignPriceToStep(
                  event.target.value,
                  contractPriceStep.price_step,
                  contractPriceStep.price_precision,
                  "down"
                )
              );
            },
          })}
          errors={errors}
        />
      )}
      <PerpsPriceInput
        placeholder={order?.price}
        minPrice={contractPriceStep.price_step}
        register={register("price", {
          disabled: loading,
          required: true,
          validate: {
            [FormValidatorKeysEnum.moreThanZero]: (v) => parseFloat(v) > 0,
            [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
              roundToStepSize(
                v,
                contractPriceStep.price_step,
                contractPriceStep.price_precision
              ) >= contractPriceStep.price_step,
          },
          onBlur(event) {
            setValue(PerpFormFieldKeyEnum.PRICE, alignPriceField(event.target.value));
          },
        })}
        errors={errors}
      />
      <PerpsSizeInput
        placeholder={unfilledAmount}
        // Floor, not step -- see IContractPriceStep.min_order_amount. Editing a spot
        // order down to a sub-minimum size would revert with OrderTooSmall.
        minAmountSize={
          perpAsset
            ? contractPriceStep.min_order_amount
            : contractPriceStep.price_step
        }
        underlyingAsset={instrument?.underlying_asset as AssetResponse}
        selectedAsset={perpAsset}
        onChangeAsset={setPerpAsset}
        register={register("amount", {
          disabled: loading,
          required: true,
          validate: {
            // In USD mode with a price, what must be above zero is the size the
            // figure buys: blur no longer snaps a sub-step figure to 0, which is
            // how "Min order size" used to show for it.
            [FormValidatorKeysEnum.moreThanZero]: (v) =>
              !perpAsset && usdContractSizeFor && Number(price) > 0
                ? Number(usdContractSizeFor(v)) > 0
                : parseFloat(v) > 0,
            [FormValidatorKeysEnum.notEnoughBalance]: verifyNotEnoughBalance,
            [FormValidatorKeysEnum.orderValueTooSmall]:
              verifyOrderValueTooSmall,
            [FormValidatorKeysEnum.decimalsTooSmall]: (v) => {
              if (perpAsset) {
                return (
                  roundToStepSize(
                    v,
                    contractPriceStep.amount_step,
                    contractPriceStep.amount_precision
                  ) >= contractPriceStep.min_order_amount
                );
              }
              return (
                roundToStepSize(
                  v,
                  contractPriceStep.price_step,
                  contractPriceStep.price_precision
                ) >=
                1 / 10 ** contractPriceStep.price_precision
              );
            },
          },
          onBlur(event) {
            const typed = String(event.target.value ?? "").trim();
            if (perpAsset) {
              setValue(
                PerpFormFieldKeyEnum.AMOUNT,
                floorSizeField(
                  typed,
                  contractPriceStep.amount_step,
                  contractPriceStep.amount_precision
                )
              );
              return;
            }
            // USD: floored to the cent and left there. The size is worked out at
            // submit as USD / price floored to the step, so it never costs more
            // than this figure; re-snapping the figure to that size's value lost
            // a cent on every blur, and zeroed a pre-filled figure that is a
            // fraction of a cent short of a whole step.
            setValue(PerpFormFieldKeyEnum.AMOUNT, floorUsdFieldValue(typed));
          },
        })}
        style={{ paddingBottom: 0, marginTop: "8px" }}
        errors={errors}
        showValidationError={showAmountValidation}
      />
    </>
  );
}
